// src/components/professor/ClassList.js
import React, { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  setDoc,
  serverTimestamp,
  doc,
  getDoc,
} from 'firebase/firestore';
import { db } from '../../../firebase';
import ClassFormModal from './ClassFormModal';
import Preloader from '../../Preloader';
import { Clipboard, Plus, Users } from 'lucide-react';

const ClassList = ({ selectClass, user, theme }) => {
  const [classes, setClasses] = useState([]);
  const [showClassModal, setShowClassModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchClasses = async () => {
    setLoading(true);
    try {
      if (!user) {
        alert('No user is logged in.');
        setLoading(false);
        return;
      }

      // Define two separate queries:
      // 1. Classes where the user is the owner
      const ownedClassesQuery = query(
        collection(db, 'classes'),
        where('professor_uid', '==', user.uid)
      );

      // 2. Classes where the user has been granted access
      const accessibleClassesQuery = query(
        collection(db, 'classes'),
        where('access_uid', 'array-contains', user.uid)
      );

      // Execute both queries in parallel
      const [ownedSnapshot, accessibleSnapshot] = await Promise.all([
        getDocs(ownedClassesQuery),
        getDocs(accessibleClassesQuery),
      ]);

      // Use a Map to avoid duplicate classes if a user is both owner and has access
      const classesMap = new Map();

      // Add owned classes to the map
      ownedSnapshot.forEach((docSnap) => {
        classesMap.set(docSnap.id, docSnap);
      });

      // Add accessible classes to the map (duplicates will be ignored)
      accessibleSnapshot.forEach((docSnap) => {
        if (!classesMap.has(docSnap.id)) {
          classesMap.set(docSnap.id, docSnap);
        }
      });

      // Prepare an array of promises to fetch enrollment counts in parallel
      const enrollmentPromises = Array.from(classesMap.values()).map(
        async (docSnap) => {
          const classData = docSnap.data();
          const enrollmentCount = await getEnrollmentCount(docSnap.id);
          return {
            id: docSnap.id,
            ...classData,
            enrollmentCount,
          };
        }
      );

      // Resolve all enrollment count promises
      const resolvedClassesData = await Promise.all(enrollmentPromises);

      // Update the state with the fetched classes
      setClasses(resolvedClassesData);
    } catch (error) {
      console.error('Error fetching classes:', error);
    } finally {
      setLoading(false);
    }
  };

  const getEnrollmentCount = async (classId) => {
    try {
      const enrollmentsDoc = await getDoc(doc(db, 'enrollments', classId));
      if (enrollmentsDoc.exists()) {
        return enrollmentsDoc.data().students.length;
      }
      return 0;
    } catch (error) {
      console.error(`Error getting enrollment count for class ${classId}:`, error);
      return 0;
    }
  };

  useEffect(() => {
    fetchClasses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddClass = async (newClass) => {
    setLoading(true);
    try {
      const classInvitation = generateInvitationCode();

      const classRef = await addDoc(collection(db, 'classes'), {
        class_name: newClass.class_name,
        professor_uid: user.uid,
        start_date: new Date(newClass.start_date),
        end_date: new Date(newClass.end_date),
        class_invitation: classInvitation,
        statsaccess: newClass.statsaccess,
        gradeaccess: newClass.gradeaccess,
        sessionaccess: newClass.sessionaccess,
        dataDeletionAck: newClass.dataDeletionAck,
        aiReporting: newClass.aiReporting,
        quizFormat: newClass.quizFormat,
        created_at: serverTimestamp(),
      });

      // Initialize enrollments and grades for the new class
      await setDoc(doc(db, 'enrollments', classRef.id), { students: [] });
      await setDoc(doc(db, 'grades', classRef.id), {});

      // Refresh classes
      await fetchClasses();

      // Close modal
      setShowClassModal(false);
    } catch (error) {
      console.error('Error adding class:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateInvitationCode = () => {
    return Math.random().toString(36).substring(2, 8).toUpperCase();
  };

  const handleCopyInvitation = (invitationCode) => {
    navigator.clipboard
      .writeText(invitationCode)
      .then(() => {
        alert('Invitation code copied to clipboard!');
      })
      .catch((error) => {
        console.error('Failed to copy invitation code:', error);
      });
  };

  if (loading) {
    return <Preloader />;
  }

  return (
    <>
      <div className="mb-4 flex justify-end">
        <button
          onClick={() => setShowClassModal(true)}
          className="inline-flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition sm:w-auto"
          style={{ backgroundColor: 'var(--primary-color)', boxShadow: 'var(--shadow-primary)' }}
          onMouseOver={(e) =>
            (e.currentTarget.style.backgroundColor = 'var(--hover-color)')
          }
          onMouseOut={(e) =>
            (e.currentTarget.style.backgroundColor = 'var(--primary-color)')
          }
        >
          <Plus size={16} aria-hidden="true" />
          Add Class
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {classes.length === 0 ? (
          <p className="col-span-full rounded-lg border p-8 text-center" style={{ color: 'var(--muted-color)', borderColor: 'var(--border-color)', backgroundColor: 'var(--card-bg)' }}>
            No classes found for this professor.
          </p>
        ) : (
          classes.map((cls) => (
            <div
              key={cls.id}
              onClick={() => selectClass(cls)}
              className="cursor-pointer rounded-lg border p-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md"
              style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--border-color)', boxShadow: 'var(--shadow-sm)' }}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="truncate text-lg font-semibold" style={{ color: 'var(--text-color)' }}>
                    {cls.class_name}
                  </h3>
                  <p className="mt-2 flex items-center gap-2 text-sm" style={{ color: 'var(--muted-color)' }}>
                    <Users size={15} aria-hidden="true" />
                    {cls.enrollmentCount} enrolled
                  </p>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-2" style={{ color: 'var(--muted-color)' }}>
                <span className="rounded-lg border px-2.5 py-1 font-mono text-xs font-semibold" style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--alt-bg)' }}>
                  {cls.class_invitation}
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCopyInvitation(cls.class_invitation);
                  }}
                  className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-white transition"
                  style={{ backgroundColor: 'var(--primary-color)' }}
                  onMouseOver={(e) =>
                    (e.currentTarget.style.backgroundColor = 'var(--hover-color)')
                  }
                  onMouseOut={(e) =>
                    (e.currentTarget.style.backgroundColor = 'var(--primary-color)')
                  }
                >
                  <Clipboard size={13} aria-hidden="true" />
                  Copy
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Class Form Modal */}
      {showClassModal && (
        <ClassFormModal
          theme={theme}
          closeModal={() => setShowClassModal(false)}
          handleAddClass={handleAddClass}
        />
      )}
    </>
  );
};

export default ClassList;
